import React, { useEffect, useState } from 'react';
import jwtDecode from 'jwt-decode';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { loginURL, apiGet } from '@src/services/api/helpers.js';

const parseURLForKeyTForToken = (queryString) => {
    const query = {};
    const pairs = (queryString[0] === '?' ? queryString.substr(1) : queryString).split('&');
    pairs.forEach((pairStr) => {
        const pair = pairStr.split('=');
        query[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || '');
    });
    return query;
};

const checkUserRole = (userOrJwt) => {
    if (!userOrJwt) return false;

    // Check if it's the user object
    if (userOrJwt.access_level) {
        return userOrJwt.access_level === 'admin';
    }

    // Check if it's a JWT string (fallback)
    if (typeof userOrJwt === 'string') {
        try {
            const decodedJWT = jwtDecode(userOrJwt);
            return decodedJWT && decodedJWT.access_level === 'admin';
        // eslint-disable-next-line no-unused-vars
        } catch (e) {
            return false;
        }
    }

    return false;
};

export const AuthLoader = () => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
        }}
    >
        <CircularProgress />
    </Box>
);

// Resolves once the signed in admin (and their permissions) is stored in local storage.
// Nothing that reads permissions may render before this returns true.
export const useAuth = () => {
    const [authenticated, setAuthenticated] = useState(false);

    useEffect(() => {
        const authenticate = async () => {
            let jwt = window.localStorage.getItem('jwt');
            const { search } = window.location;
            const parse = parseURLForKeyTForToken(search);

            // 1. Check for token in URL
            if (!jwt && parse.t) {
                jwt = parse.t;
                window.localStorage.setItem('jwt', jwt);
            }

            // 2. If no token, redirect to login
            if (!jwt) {
                window.location.replace(loginURL(window.location.href));
                return;
            }

            // 3. We have a token. Fetch user info to validate role and get permissions.
            try {
                // First check if we have a legacy token that has access_level (opt)
                // But generally we should fetch the user to be sure.
                const response = await apiGet('/user');
                const { user } = response.data;

                if (!checkUserRole(user)) {
                    window.location.replace('/forbidden');
                    return;
                }

                window.localStorage.setItem('user_info', JSON.stringify(user));

                // If we just logged in via URL token, clean the URL.
                // Keep the app unmounted, the reload below renders it from scratch.
                if (parse.t) {
                    window.location.replace(window.location.href.split('?')[0]);
                    return;
                }

                setAuthenticated(true);
            } catch (error) {
                // If fetch fails (e.g. 401), or logic fails
                console.error('Auth check failed', error);

                // Fallback: check JWT directly (legacy) if API failed?
                // If API failed with 401, token is bad.
                // If API failed with network error, maybe fallback?
                // But minimal token has no access_level, so fallback checkUserRole(jwt) will fail anyway.
                // So we must remove old token and redirect to login.
                window.localStorage.removeItem('jwt');
                window.localStorage.removeItem('user_info');

                window.location.replace(loginURL(window.location.href));
            }
        };

        authenticate();
    }, []);

    return authenticated;
};
